/**
 * URLs externas das plataformas de pagamento/e-commerce
 */
export const EXTERNAL_URLS = {
  guru: {
    /**
     * URL de transação no Digital Manager Guru
     * @param orderId - marketplace_id do pagamento
     * @param marketplace - nome do gateway (mercadopago, pagarme, stripe, etc.)
     */
    order: (orderId: string, marketplace: string) =>
      `https://digitalmanager.guru/admin/transactions/${marketplace}/${orderId}`,
  },
  yampi: {
    /**
     * URL do pedido no painel Yampi
     * @param orderId - ID do pedido
     */
    order: (orderId: string) => `https://app.yampi.com.br/orders/${orderId}/details`,
  },
} as const

export type ReferralSource = 'guru' | 'yampi'

/**
 * Gera a URL do pedido externo baseado na plataforma de origem
 *
 * @param source - Plataforma de origem ('guru' | 'yampi' | null)
 * @param orderId - ID do pedido externo
 * @param marketplace - Nome do marketplace (apenas para Guru)
 * @returns URL do pedido ou null se source não for reconhecido
 *
 * @example
 * getOrderUrl('guru', '12345', 'mercadopago')
 * // => 'https://digitalmanager.guru/admin/transactions/mercadopago/12345'
 *
 * getOrderUrl('yampi', '67890')
 * // => 'https://app.yampi.com.br/orders/67890/details'
 *
 * getOrderUrl(null, '12345')
 * // => null (dados históricos sem source)
 */
export function getOrderUrl(
  source: ReferralSource | null | undefined,
  orderId: string,
  marketplace?: string | null
): string | null {
  if (!source) return null

  if (source === 'guru') {
    // Guru requer marketplace para construir a URL
    if (!marketplace) return null
    return EXTERNAL_URLS.guru.order(orderId, marketplace)
  }

  if (source === 'yampi') {
    return EXTERNAL_URLS.yampi.order(orderId)
  }

  return null
}

/**
 * Retorna o nome legível da plataforma
 */
export function getSourceLabel(source: ReferralSource | null | undefined): string {
  if (!source) return 'Desconhecido'

  const labels: Record<ReferralSource, string> = {
    guru: 'Digital Manager Guru',
    yampi: 'Yampi',
  }

  return labels[source]
}
